// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams } from "react-router-dom";
import HomePage from "./pages/Home";
import TravelPage from "./pages/Travel";
import Test from "./pages/Test";
import "./App.css";
import AuthModal from "./components/AuthModal";
import Navbar from "./components/Navbar";
import TripModal from "./components/TripModal";
import Footer from "./components/Footer";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import { setFavicon, setTitle } from "./utils/setMeta";
import { addTrip } from "./services/trips";
import { useAuth } from "./context/AuthContext";
import { normalizeHandle } from "./utils/handle";
import { auth } from "./firebase";
import { useTheme } from "./context/ThemeContext";
import ExplorePage from "./pages/Explore";
import { ToastProvider } from "./components/ToastProvider";
import AiTripDetails from "./pages/AiTripDetails";
import { AiTripProvider } from "./context/AiTripContext";

const DEFAULT_API = "https://us-central1-travel-6c761.cloudfunctions.net/api";
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.REACT_APP_USE_EMULATOR === "true"
    ? process.env.REACT_APP_EMULATOR_URL || "http://127.0.0.1:5001/travel-6c761/us-central1/api"
    : DEFAULT_API);

function TravelPageWithHandle() {
  const params = useParams();
  const rawHandleParam =
    typeof params.handle === "string"
      ? params.handle
      : typeof params.maybeHandle === "string"
        ? params.maybeHandle
        : typeof params.slug === "string" && !params.handle
          ? params.slug
          : "";
  const rawSlugParam =
    typeof params.slug === "string" && typeof params.handle === "string" ? params.slug : undefined;

  const rawHandle = typeof rawHandleParam === "string" ? decodeURIComponent(rawHandleParam) : "";
  const rawSlug = typeof rawSlugParam === "string" ? decodeURIComponent(rawSlugParam) : "";

  let handle = "";
  let slug = "";

  if (rawSlug) {
    handle = rawHandle.replace(/^@+/, "").replace(/^\/+|\/+$/g, "");
    slug = rawSlug.replace(/^\/+|\/+$/g, "");
  } else {
    const seg = rawHandle.replace(/^\/+|\/+$/g, "");
    if (!seg) {
      handle = "";
      slug = "";
    } else if (seg.startsWith("@")) {
      handle = seg.replace(/^@+/, "");
      slug = "";
    } else if (seg.includes("-")) {
      handle = "";
      slug = seg;
    } else {
      handle = seg;
      slug = "";
    }
  }

  const normalized = handle ? normalizeHandle(handle) : null;

  if (!normalized && !slug) {
    return <HomePage />;
  }

  return <TravelPage externalHandle={normalized} externalSlug={slug || null} />;
}

function NavbarWithNavigate({ onOpenAuth, onAddTrip }) {
  const navigate = useNavigate();

  const handleViewTrips = (handle) => {
    if (!handle) {
      navigate("/");
      return;
    }
    const raw = String(handle).replace(/^@/, "");
    const normalized = normalizeHandle(raw);
    if (!normalized) {
      navigate("/");
      return;
    }
    navigate(`/@${normalized}`);
  };

  const handleNavigateHome = () => {
    navigate("/");
  };

  return (
    <Navbar
      onOpenAuth={onOpenAuth}
      onAddTrip={onAddTrip}
      onViewTrips={handleViewTrips}
      onNavigateHome={handleNavigateHome}
    />
  );
}

function App() {
  const [authMode, setAuthMode] = useState(null);
  const [showAddTripModal, setShowAddTripModal] = useState(false);
  const [savingTrip, setSavingTrip] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const { currentUser } = useAuth();
  const { theme } = useTheme();

  useEffect(() => {
    if (currentUser) {
      setTitle(`Jift | Welcome ${currentUser.displayName || "User"}`);
    } else {
      setTitle("Jift | Travel Planner");
    }
    setFavicon(theme === "dark" ? "/favicon-dark32.png" : "/favicon-light32.png");
  }, [currentUser, theme]);

  useEffect(() => {
    const onOpenFromAnywhere = () => setShowAddTripModal(true);
    window.addEventListener("jift:openAddTrip", onOpenFromAnywhere);
    return () => window.removeEventListener("jift:openAddTrip", onOpenFromAnywhere);
  }, []);


  const openAuth = (mode) => setAuthMode(mode);
  const closeAuth = () => setAuthMode(null);

  const openAddTrip = () => {
    if (!currentUser?.uid) {
      setAuthMode("login");
      return;
    }
    setShowAddTripModal(true);
  };

  const closeAddTrip = () => {
    setShowAddTripModal(false);
    setSaveError(null);
  };

  const handleSaveTrip = async (tripData) => {
    if (!currentUser?.uid) {
      setSaveError("You must be logged in to create a trip.");
      setAuthMode("login");
      return;
    }

    setSavingTrip(true);
    setSaveError(null);

    try {
      const id = await addTrip(tripData, currentUser.uid);
      closeAddTrip();
      return id;
    } catch (err) {
      console.error("App - handleSaveTrip create error:", err);
      setSaveError(err?.message || "Failed to save trip.");
      throw err;
    } finally {
      setSavingTrip(false);
    }
  };

  const ownerHandle =
    currentUser && (currentUser.displayName || currentUser.email || currentUser.uid)
      ? normalizeHandle(
        currentUser.displayName ||
        (currentUser.email ? currentUser.email.split("@")[0] : "") ||
        (currentUser.uid || "")
      )
      : null;

  return (
    <BrowserRouter>
      <ToastProvider>
        <AiTripProvider>
          <NavbarWithNavigate onOpenAuth={openAuth} onAddTrip={openAddTrip} />

          <main style={{ minHeight: "calc(100vh - 160px)" }}>
            <Routes>
              <Route path="/debug" element={<div>Debug route</div>} />
              <Route path="/test" element={<Test />} />
              <Route path="/" element={<HomePage />} />

              <Route path="/travel/:handle/:slug?" element={<TravelPageWithHandle />} />
              <Route path="/travel/:slug" element={<TravelPageWithHandle />} />

              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />

              <Route path="/:maybeHandle/:slug?" element={<TravelPageWithHandle />} />

              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/ai-trip" element={<AiTripDetails />} />

              <Route path="*" element={<HomePage />} />
            </Routes>
          </main>

          {authMode && (
            <AuthModal mode={authMode} onClose={closeAuth} onSwitch={(m) => setAuthMode(m)} />
          )}

          <TripModal
            open={showAddTripModal}
            onClose={closeAddTrip}
            onSave={handleSaveTrip}
            saving={savingTrip}
            currentUserId={currentUser?.uid || null}
            ownerHandle={ownerHandle}
          />

          {saveError && (
            <div
              style={{
                position: "fixed",
                bottom: 20,
                right: 20,
                background: "#ffe6e6",
                padding: 12,
                borderRadius: 8,
              }}
            >
              <strong>Error:</strong> {saveError}
            </div>
          )}

          <Footer />
        </AiTripProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
